import type { FC } from 'react';
import type { TFunction } from 'i18next';
import type { Control, FieldErrors } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { Button, Flex, Form } from 'antd';
import TextArea from 'antd/es/input/TextArea';

import type { UpdateFormValues } from '@modules/ai-agent/lib/types';
import { Text } from '@shared/ui/antd';

import { DeleteOutlinedSmall, PlusBackground } from '@shared/assets/icons/components';

import styles from '../../prompt.module.scss';

interface ContactFormPromptsProps {
  promptFields: { id: string }[];
  control: Control<UpdateFormValues>;
  errors: FieldErrors<UpdateFormValues>;
  isLoading: boolean;
  placeholders: string[];
  onAddPrompt: () => void;
  onRemovePrompt: (index: number) => void;
  t: TFunction<'translation', 'prompt'>;
}

export const ContactFormPrompts: FC<ContactFormPromptsProps> = ({
  promptFields,
  control,
  errors,
  isLoading,
  placeholders,
  onAddPrompt,
  onRemovePrompt,
  t,
}) => (
  <Flex vertical className={styles.element} gap={16}>
    <Flex gap={4} align="start" vertical>
      <Text variant="md" fontWeight={500}>
        {t('contactForm.title')}
      </Text>

      <Text variant="sm" style={{ color: 'var(--color-text-tertiary)' }}>
        {t('contactForm.description')}
      </Text>
    </Flex>

    <Flex vertical className={styles.element_inside} gap={12}>
      <Flex vertical gap={12} style={{ width: '100%' }}>
        {promptFields.map((field, index) => (
          <Flex key={field.id} gap={12} className={styles.stageRow}>
            <div className={styles.singleColumnRowContent}>
              <div className={styles.stagePrimaryCol}>
                <Text variant="sm" fontWeight={400} className={styles.stageLabel}>
                  {index + 1}. {t('contactForm.conditionLabel')}
                </Text>

                <Form.Item
                  help={errors.show_form_prompts?.[index]?.message}
                  validateStatus={errors.show_form_prompts?.[index] ? 'error' : ''}
                  className={styles.label}
                >
                  <Controller
                    control={control}
                    name={`show_form_prompts.${index}`}
                    render={({ field }) => (
                      <TextArea
                        {...field}
                        id={`show-form-prompts-${index}`}
                        placeholder={placeholders[index] ?? placeholders.at(-1)}
                        className={styles.stageArea}
                        style={{ resize: 'none' }}
                        autoSize={{ minRows: 2 }}
                        disabled={isLoading}
                      />
                    )}
                  />
                </Form.Item>
              </div>
            </div>

            <Flex gap={8} className={styles.stageActions}>
              <button
                type="button"
                className={styles.stageActionButton}
                onClick={() => onRemovePrompt(index)}
                aria-label="Remove contact form condition"
              >
                <DeleteOutlinedSmall color="#A7A8AE" />
              </button>
            </Flex>
          </Flex>
        ))}
      </Flex>

      <Button
        icon={<PlusBackground fill="var(--color-text-secondary)" />}
        iconPosition="end"
        onClick={onAddPrompt}
        type="text"
        className={styles.button_additional}
      >
        {t('contactForm.addButton')}
      </Button>
    </Flex>
  </Flex>
);
